import { CouponNumberGroup } from '@/components/CouponNumberGroup';
import { Breakpoint } from '@/components/GameBreakpoint';
import { Icon, IconVariant } from '@/components/Icon';
import { CouponNumberGroupConfig } from '@/config/game';
import { CouponState } from '@/reducers/coupon';
import classnames from '@/utilities/classnames';
import setFocusIfElementExists from '@/utilities/setFocusIfElementExists';
import React from 'react';

export interface CouponConfig {
  numberGroups: CouponNumberGroupConfig[];
  columns: number;
  quickTipPerCoupon?: boolean;
  quickTips?: number[];
}

export interface CouponProps extends CouponConfig {
  index: number;
  crossedNumbers: CouponState;
  disabled?: boolean;
  ariaHidden?: boolean;
  onClick?: (indexCoupon: number) => void;
  onClickCouponNumber?: (
    indexCoupon: number,
    indexGroup: number,
    value: number
  ) => void;
  onClear: (indexCoupon: number) => void;
  showActionButtons: boolean;
  viewSize: Breakpoint;
  layout?: 'side-by-side' | 'below-each-other';
}

export class Coupon extends React.Component<CouponProps> {
  componentDidMount(): void {
    if (this.props.columns === 1) {
      setFocusIfElementExists('.CouponNumberGroup__numbers > div:nth-child(1)');
    }
  }

  handleOnClickCoupon = ($event) => {
    if (this.props.onClick) {
      $event.stopPropagation();
      this.props.onClick(this.props.index);
    }
  };

  handleOnClickCouponNumber = (indexGroup: number, value: number) => {
    if (this.props.onClickCouponNumber) {
      this.props.onClickCouponNumber(this.props.index, indexGroup, value);
    }
  };

  couponGroupIsFilled = (groupId: number): boolean => {
    return (
      this.props.crossedNumbers &&
      this.props.crossedNumbers[groupId] &&
      this.props.crossedNumbers[groupId].length >=
        this.props.numberGroups[groupId].minCrossed
    );
  };

  private get isSingleCoupon(): boolean {
    return this.props.columns === 1;
  }

  render(): JSX.Element {
    const width = `${100 / this.props.columns}%`;
    const style = {
      flex: `0 0 ${width}`,
      maxWidth: this.isSingleCoupon ? `calc(${width}% + .4em)` : width,
      width: this.isSingleCoupon ? `calc(${width}% + .4em)` : width,
    };
    const className = classnames({
      Coupon: true,
      'Coupon--disabled': this.props.disabled,
      'Coupon--fade-in': this.props.viewSize >= Breakpoint.Game,
      'Coupon--single': this.isSingleCoupon,
    });

    const couponProps = {
      className,
      style,
      title: `Spielfeld ${this.props.index}`,
    };

    if (!this.props.disabled) {
      couponProps['onClick'] = this.handleOnClickCoupon;
    }

    return (
      <div {...couponProps}>
        <div
          className="Coupon__numberGroups"
          data-coupon={this.props.index}
          data-layout={this.props.layout || 'n/a'}
          tabIndex={this.props.disabled ? -1 : 0}
          aria-label={
            this.props.ariaHidden
              ? `Klicken um Spielfeld ${this.props.index} auszufüllen`
              : undefined
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.handleOnClickCoupon(event);
            }
          }}
        >
          {this.props.numberGroups.map(
            (numberGroup: CouponNumberGroupConfig, groupId: number) => {
              const { columns, columnsVariant, ...restProps } = numberGroup;

              const couponNumberGroupColumns =
                columnsVariant && this.props.viewSize !== Breakpoint.Game
                  ? columnsVariant
                  : columns;

              if (
                this.props.crossedNumbers &&
                this.props.crossedNumbers[groupId]
              )
                return (
                  <React.Fragment key={groupId}>
                    {groupId > 0 && (
                      <div className="CouponNumberGroup__plus">
                        <Icon variant={IconVariant.Plus} />
                      </div>
                    )}
                    <CouponNumberGroup
                      index={groupId}
                      isFilled={this.couponGroupIsFilled(groupId)}
                      couponIndex={this.props.index.toString()}
                      onClickCouponNumber={this.handleOnClickCouponNumber}
                      crossedNumbers={this.props.crossedNumbers[groupId]}
                      columns={couponNumberGroupColumns}
                      ariaHidden={this.props.ariaHidden}
                      {...restProps}
                    />
                  </React.Fragment>
                );
            }
          )}
        </div>
      </div>
    );
  }
}
